// React and related imports
import React, { useMemo } from 'react';

// Flowbite components
import { Table } from 'flowbite-react';

// Custom Components
import { TableCell } from '../Table/Table';
import { EditButton } from '../Shared/EditButton';

// Redux selectors, hooks, and actions
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import {
  openContentModal,
  setSelectedContent,
} from '../../redux/slices/contentSlice';

// Types and Interfaces
import { IContent } from '../../common/interfaces/Content.interface';
import { ContentContextTypes } from '../../common/enums/ContentContextTypes';

// i18 Translation
import { TFunction } from 'i18next';

// Utils
import { truncateText } from '../../utils/truncateText';
import { shortenLink } from '../../utils/shortenLink';
import { getTitleTooltip } from '../../utils/getTitleTooltip';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';
import { getLocalizedValueFromMultiLangValues } from '../../utils/getLocalizedValueFromMultiLangValues';

interface ContentTableBodyProps {
  t: TFunction<'translation', undefined>;
  tableData: IContent[];
  contextType: ContentContextTypes;
}

export const ContentTableBody: React.FC<ContentTableBodyProps> = ({
  t,
  tableData,
  contextType,
}) => {
  const dispatch = useAppDispatch();
  const noDataPlaceholder = '--';
  const isRtl = useAppSelector(selectIsRTL);

  const rows = useMemo(() => {
    return tableData.map((content) => {
      const title = getLocalizedValueFromMultiLangValues(content.title);
      const truncatedTitle =
        truncateText(title || '', 30, 'ar', isRtl) || noDataPlaceholder;
      const mediaUrl = content.media?.url;
      const shortenedUrl = mediaUrl
        ? shortenLink(mediaUrl, 20, isRtl)
        : noDataPlaceholder;

      return {
        id: content.id,
        title,
        truncatedTitle,
        shortenedUrl,
        mediaUrl,
        contentType:
          t(`contentTypes.${content.contentType}`) || noDataPlaceholder,
        content,
      };
    });
  }, [tableData, t]);

  const handleEditClick = (content: IContent) => {
    dispatch(setSelectedContent({ ...content, contextType }));
    dispatch(openContentModal());
  };

  return (
    <Table.Body>
      {rows.map(
        ({
          id,
          title,
          truncatedTitle,
          shortenedUrl,
          mediaUrl,
          contentType,
          content,
        }) => (
          <Table.Row key={id} className="bg-white">
            <TableCell>{getTitleTooltip(title, truncatedTitle)}</TableCell>
            <TableCell>{contentType}</TableCell>
            <TableCell>
              {mediaUrl ? (
                <a
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                >
                  {shortenedUrl}
                </a>
              ) : (
                noDataPlaceholder
              )}
            </TableCell>
            <TableCell>
              {content.updatedAt
                ? new Date(content.updatedAt).toLocaleString()
                : noDataPlaceholder}
            </TableCell>
            <TableCell>
              <EditButton onClick={() => handleEditClick(content)} />
            </TableCell>
          </Table.Row>
        )
      )}
    </Table.Body>
  );
};
